import type {
  OnboardingNudgeKey,
  OnboardingNudgePage,
  OnboardingNudgeResponse,
} from '@wundo/shared/onboardingNudge.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

/**
 * The onboarding nudge of the page on screen.
 *
 * Asked per page rather than once at sign-in: whether a nudge applies depends
 * on what the page is about and on work that may have been done a minute ago.
 * The backend decides everything — the four-week window, the trigger, the exit
 * and the dismissals — so this only says where the drawer is standing.
 *
 * Not the meeting nudge: that one is the reminder Wundo sends over Teams or
 * e-mail before a meeting, and never reaches the browser.
 */
export async function fetchOnboardingNudge(
  userId: string,
  page: OnboardingNudgePage,
): Promise<OnboardingNudgeResponse> {
  const query = new URLSearchParams({ page: page.page });
  if ('meeting_id' in page) query.set('meeting_id', page.meeting_id);

  return await apiFetchJson<OnboardingNudgeResponse>(
    `/users/${userId}/nudges/current?${query.toString()}`,
  );
}

/** "Not now." Three of these and the nudge is gone for good. */
export async function dismissOnboardingNudge(
  userId: string,
  key: OnboardingNudgeKey,
): Promise<void> {
  await apiFetch(`/users/${userId}/nudges/actions/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

/**
 * The nudge was taken up.
 *
 * Only for a button that leads somewhere else in the app: a button that sends a
 * message says what it was on the message itself, and the backend reads it
 * there.
 */
export async function reportOnboardingNudgeClicked(
  userId: string,
  key: OnboardingNudgeKey,
): Promise<void> {
  await apiFetch(`/users/${userId}/nudges/actions/clicked`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

/** Where a page's nudge is cached, so dismissing one can settle it again. */
export function onboardingNudgeKey(
  userId: string | undefined,
  page: OnboardingNudgePage,
): unknown[] {
  return [
    'onboardingNudge',
    userId,
    page.page,
    'meeting_id' in page ? page.meeting_id : null,
  ];
}
